import React from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../utils/dayjs';

const formatDate = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : String(value);
};

const { Text } = Typography;

/**
 * PatientInfoCard - Reusable component for displaying patient information
 *
 * @param {Object} props
 * @param {string} props.patientFullName - Patient initials (legacy prop name)
 * @param {string} props.patientInitials - Patient initials
 * @param {string} props.patientDateOfBirth - Patient's date of birth (ISO string or date)
 * @param {string|number} props.patientYearOfBirth - Fallback when full DOB is unavailable
 * @param {boolean} props.showCard - Whether to wrap in a Card component (default: true)
 * @param {Object} props.cardProps - Additional props to pass to Card component
 * @param {string} props.className - Additional CSS class name
 * @param {Object} props.style - Additional inline styles
 */
const PatientInfoCard = ({
  patientFullName,
  patientInitials,
  patientDateOfBirth,
  patientYearOfBirth,
  showCard = true,
  cardProps = {},
  className,
  style
}) => {
  const { t } = useTranslation();

  const displayName = patientFullName || patientInitials;
  const hasDob = !!patientDateOfBirth;
  const hasYob = patientYearOfBirth !== undefined && patientYearOfBirth !== null && patientYearOfBirth !== '';
  const hasPatientInfo = displayName || hasDob || hasYob;

  if (!hasPatientInfo) {
    return null;
  }

  const content = (
    <Row gutter={[16, 8]} style={{ width: '100%' }}>
      {displayName && (
        <Col xs={24} sm={24} md={8} lg={8}>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
              {t('Initials')}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {displayName}
            </Text>
          </div>
        </Col>
      )}
      {hasDob && (
        <Col xs={24} sm={12} md={8} lg={8}>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
              {t('Date of Birth')}
            </Text>
            <Text style={{ fontSize: 14 }}>
              {typeof patientDateOfBirth === 'string' ? formatDate(patientDateOfBirth) : patientDateOfBirth}
            </Text>
          </div>
        </Col>
      )}
      {!hasDob && hasYob && (
        <Col xs={24} sm={12} md={8} lg={8}>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
              {t('Year of Birth')}
            </Text>
            <Text style={{ fontSize: 14 }}>
              {patientYearOfBirth}
            </Text>
          </div>
        </Col>
      )}
    </Row>
  );

  if (!showCard) {
    return (
      <div className={className} style={style}>
        {content}
      </div>
    );
  }

  return (
    <Card
      size="small"
      className={className}
      style={{
        marginBottom: 16,
        ...style
      }}
      bodyStyle={{ padding: '12px 16px' }}
      {...cardProps}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <UserOutlined style={{ fontSize: 16, color: '#1890ff', marginTop: 2, flexShrink: 0 }} />
        {content}
      </div>
    </Card>
  );
};

export default PatientInfoCard;
